/** The itinerary record's Days card (plan §5 "Days card", O7; §10.3 `days/`; key `3`, full
 *  width): the Day ribbon on top — the trip's shape, a segment per day — and under it the days
 *  as a days-only records outline (one level, `day:<id>` records on the itinerary's `days` list;
 *  no stops, no inspector), one day opening in place under its row.
 *
 *  - **Header:** the card's title, and its caption — "8 rows · 12 days", with an info note when
 *    the span total disagrees with the duration display ("Duration says 12 days"). The header
 *    keeps a small button's height for edit mode's Add day.
 *  - **Open in place:** one day at a time. Enter (or a click) on a row opens it, and again
 *    closes it; ↑ / ↓ / Home / End move between rows — the outline is one tab stop. On the
 *    ribbon ← / → move between days and Enter (or a click) opens that day and takes the focus to
 *    its row. Opening a day grows the card below that row only: the ribbon never moves.
 *  - **Never virtualised:** the open day is taller than a row, and the virtualiser assumes fixed
 *    row heights (an itinerary holds at most 365 rows).
 *  - **Live structure** (edit mode, allowed — `dayOps`, each one undo step): **Add day** in the
 *    header's 24 px reserve (appends; the new day opens, its title focused; never past the
 *    365-day cap), and per row ⋮ (Shift+F10): Insert a day after, Move up / down, Move to …,
 *    Remove. Alt+↑ / Alt+↓ move a day, Delete removes it, a drag by its handle puts it in
 *    another's place. A removed saved day stays, struck, with Restore, until the save (on the
 *    wire its six fields go to null, each compare-and-set); a new day simply goes. A polite
 *    live region says each one ("Added Day 9", "Moved “Bangkok” to Day 2", "Removed Day 2–3 ·
 *    Ayutthaya") — and what the page tells it (`DaysChannel`: days a save brought back).
 *  - **Verdicts:** a day's title verdict (§7, "<Source>'s next sync deletes this day") comes
 *    from edit_context — or, for a retitled day, from the preview of its new title — so it is
 *    edit mode's, in the row's reserved 16 px slot (an icon; its words in the row's name and
 *    tooltip); view mode keeps the room on a re-synced itinerary.
 *  - **After a save** the new days carry their real ids (`DaysChannel.created`): the open day
 *    follows. A ledger row for a closed day's field opens the day, then focuses the field. */
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, ListSubheader, MenuItem, Skeleton, Tooltip, Typography } from '@mui/material';
import { memo, useCallback, useEffect, useId, useMemo, useRef, useState, type CSSProperties } from 'react';

import type { ItineraryDetail } from '@/api/itineraries';
import { SectionCard } from '@/components/ReadView';
import { initialDraftState, parseRecordKey, type RecordKey } from '@/features/records/draft/changeSet';
import { useDraftStable } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import type { RecordVerdict } from '@/features/records/editor/types';
import { RestoreButton, visuallyHidden } from '@/features/records/fields/RowMarks';
import { RecordOutline, type OutlineDrag, type OutlineSelection } from '@/features/records/outline/Outline';
import { OutlineRowMenu, type OutlineRowProps } from '@/features/records/outline/OutlineRowShell';
import type { OutlineOpsContext } from '@/features/records/outline/ops';
import { RecordCardSkeleton } from '@/features/records/page/RecordStates';
import { OutlineProvider, useDraftSelector, useOutline, useOutlineField, type OutlineContextValue } from '@/features/records/outline/store';
import { listIn, type Baselines } from '@/features/records/outline/tree';
import type { ImageChunks } from '@/utils/imageUrl';

import { DayRibbon, DOT_ROOM_PX } from '../parts/DayRibbon';
import { RIBBON_HEIGHT_PX, type DayMarks } from '../parts/dayRibbonModel';
import { itineraryRec } from '../viewConfig';

import { addDay, addDayRefusal, moveDay, removeDay, restoreDay, type DayEdit } from './dayOps';
import { DAY_ROW_PX, DayRow } from './DayRow';
import type { DaysChannel } from './daysChannel';
import {
  DAYS_OUTLINE,
  DAYS_PATH,
  ITINERARY_MEMBERSHIPS,
  dayLabel,
  dayRec,
  dayRowName,
  dayRows,
  dayRowsKey,
  daysCaption,
  durationMismatch,
  feedMatchesDaysByTitle,
  ribbonInput,
  ribbonKey,
  storedDayRanges,
  totalDays,
  type DayRowModel,
} from './daysModel';

/** The header's height: a small button's (edit mode's Add day), kept in view mode. */
export const DAYS_HEADER_PX = 24;

const LABELS = {
  nav: 'Day outline',
  tree: 'Days of the itinerary',
  help: 'Arrow keys move between days, and Enter opens or closes one.',
  liveHelp: 'Alt with an arrow moves a day, Delete removes it, Shift+F10 opens its actions.',
  empty: 'No days yet.',
  liveEmpty: 'Add the first day above.',
} as const;

/** Dragging a day (pointer; Alt+arrows and the row menu are the keyboard path). */
const DRAG_INSTRUCTIONS =
  'To move a day, drop it on another day to put it in that place. From the keyboard: Alt with an arrow moves a day.';

/** A day's title verdict, given whether the draft retitled it (null: none to show). */
export type DayVerdictOf = (dayId: number, retitled: boolean) => RecordVerdict | null;

const rowHeight = () => DAY_ROW_PX;

/** The rows a loading Days card draws when nothing says how many days it has. */
export const DAYS_SKELETON_ROWS = 3;
/** The most rows a loading Days card draws (an itinerary holds at most 365 rows). */
const DAYS_SKELETON_MAX_ROWS = 365;

export interface DaysCardProps {
  detail: ItineraryDetail;
  /** What the page's draft holds as stored (`itineraryBaselines`, or edit_context's). */
  baselines: Baselines;
  anchorId: string;
  title: string;
  /** The operator may change the days (edit mode makes the outline live). */
  canEdit?: boolean;
  /** What the page tells the card (sentences, a field to open, the saved days' ids). */
  channel?: DaysChannel;
}

/** The editor's state the card reads, once here — the mode, each day's title verdict (the
 *  preview's for a retitled day, else edit_context's), the focus registry — so the body
 *  renders only when one of them changes, never per keystroke. */
export function DaysCard(props: DaysCardProps) {
  const { mode, config, preview, fieldOf, focusField } = useRecordEditor();
  const outcomes = preview.data?.changes;
  const dayVerdict = useMemo<DayVerdictOf>(() => {
    const titleField = fieldOf('day', 'title');
    const byKey = new Map((outcomes ?? []).map((c) => [c.key, c.verdict ?? null]));
    return (dayId, retitled) => {
      const rec = dayRec(dayId);
      if (retitled && byKey.has(`${rec}:title`)) return byKey.get(`${rec}:title`) ?? null;
      return titleField ? config.verdictOf(rec, titleField, initialDraftState) : null;
    };
  }, [config, outcomes, fieldOf]);
  return <DaysCardBody {...props} editing={mode === 'edit'} dayVerdict={dayVerdict} focusField={focusField} />;
}

const DaysCardBody = memo(function DaysCardBody({
  detail,
  baselines,
  anchorId,
  title,
  canEdit = false,
  channel,
  editing,
  dayVerdict,
  focusField,
}: DaysCardProps & { editing: boolean; dayVerdict: DayVerdictOf; focusField: (rec: RecordKey, path: string) => void }) {
  const ownerRec = itineraryRec(detail.id);
  const outline = useMemo<OutlineContextValue>(
    () => ({ baselines, ownerRec, spec: DAYS_OUTLINE, memberships: ITINERARY_MEMBERSHIPS, editing, canEdit }),
    [baselines, ownerRec, editing, canEdit],
  );
  const captionId = useId();
  // The outline is the ribbon's text equivalent: it describes the ribbon.
  const outlineId = useId();
  const rootRef = useRef<HTMLElement>(null);
  const [openDay, setOpenDay] = useState<number | null>(null);
  // A day's stored image as the detail has it (edit_context's baselines hold its Image id).
  const imageOf = useCallback((dayId: number) => detail.days.find((d) => d.id === dayId)?.image ?? null, [detail]);

  // ---- the live region: what a structural edit (or the page) says ------------------------
  const [said, setSaid] = useState<{ text: string; n: number }>({ text: '', n: 0 });
  const say = useCallback((text: string) => setSaid((s) => ({ text, n: s.n + 1 })), []);

  // ---- a field of a closed day (a ledger row): open it, then focus the field -------------
  const pendingField = useRef<{ rec: RecordKey; path: string } | null>(null);
  useEffect(() => {
    if (!pendingField.current || openDay === null) return undefined;
    const raf = requestAnimationFrame(() => {
      const pending = pendingField.current;
      pendingField.current = null;
      if (pending) focusField(pending.rec, pending.path);
    });
    return () => cancelAnimationFrame(raf);
  }, [openDay, focusField]);

  useEffect(() => {
    if (!channel) return undefined;
    const offSaid = channel.said.listen(say);
    const offOpen = channel.open.listen(({ rec, path }) => {
      pendingField.current = { rec, path };
      setOpenDay(parseRecordKey(rec).id);
    });
    // The open day follows its new id once a save created it.
    const offCreated = channel.created.listen((ids) => setOpenDay((cur) => (cur !== null ? (ids.get(cur) ?? cur) : cur)));
    return () => {
      offSaid();
      offOpen();
      offCreated();
    };
  }, [channel, say]);

  // A row: Enter or a click opens it, and closes the open one.
  const onSelect = useCallback((s: OutlineSelection<'day'>) => {
    setOpenDay((cur) => (s === null || cur === s.id ? null : s.id));
  }, []);

  // ---- structural edits: one undo step each, said in the live region --------------------
  // Against the draft as it stands at the click (never a render-time snapshot).
  const { store, dispatch } = useDraftStable();
  const edit = useCallback(
    (op: (ctx: OutlineOpsContext) => DayEdit | null): DayEdit | null => {
      const done = op({ state: store.getState(), baselines, ownerRec, spec: DAYS_OUTLINE, memberships: ITINERARY_MEMBERSHIPS });
      if (!done) return null;
      dispatch(done.action);
      say(done.said);
      return done;
    },
    [store, dispatch, baselines, ownerRec, say],
  );
  const onAdd = useCallback(
    (afterId?: number) => {
      const done = edit((ctx) => addDay(ctx, afterId === undefined ? {} : { afterId }));
      if (!done) return;
      // The new day opens, its title focused (it mounts on the next frame).
      pendingField.current = { rec: dayRec(done.dayId), path: 'title' };
      setOpenDay(done.dayId);
    },
    [edit],
  );
  // The ribbon: opens the day and takes the focus to its row.
  const onRibbonOpen = useCallback((dayId: number) => {
    setOpenDay(dayId);
    requestAnimationFrame(() => {
      rootRef.current?.querySelector<HTMLElement>(`[data-itin-row="${dayRec(dayId)}"]`)?.focus();
    });
  }, []);

  return (
    <Box
      ref={rootRef}
      component="section"
      id={anchorId}
      tabIndex={-1}
      aria-label={title}
      aria-describedby={captionId}
      data-stage-panel="days"
      data-days-card
      sx={{
        scrollMarginTop: 'var(--record-spine-height, 0px)',
        outline: 'none',
        borderRadius: 1,
        '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
        '& > .MuiCard-root': { mb: 0 },
      }}
    >
      <OutlineProvider value={outline}>
        <SectionCard
          title={title}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <DaysCaption id={captionId} ownerRec={ownerRec} />
              {editing && canEdit && <AddDayButton ownerRec={ownerRec} onAdd={onAdd} />}
            </Box>
          }
        >
          <Box sx={{ display: 'grid', gap: 1.5, minWidth: 0 }}>
            <DaysRibbon ownerRec={ownerRec} openDay={openDay} onOpen={onRibbonOpen} describedBy={outlineId} />
            <DaysOutline
              id={outlineId}
              ownerRec={ownerRec}
              openDay={openDay}
              onSelect={onSelect}
              verdictRoom={feedMatchesDaysByTitle(detail.source)}
              imageOf={imageOf}
              edit={edit}
              onAdd={onAdd}
              dayVerdict={dayVerdict}
            />
          </Box>
        </SectionCard>
        {/* What a structural edit did, said politely (the outline keeps the focus). */}
        <Box aria-live="polite" aria-atomic="true" data-days-live sx={visuallyHidden}>
          <span key={said.n}>{said.text}</span>
        </Box>
      </OutlineProvider>
    </Box>
  );
});

/** The Days card while the itinerary loads, on the loaded card's geometry: the header's
 *  24 px (its caption; edit mode's Add day), the ribbon's dot room over its 28 px row, then
 *  `rows` 48 px rows between hairlines — the count the page knows before the detail (null:
 *  three; none: the outline's empty row). */
export function DaysCardSkeleton({ rows }: { rows: number | null }) {
  const n = Math.min(rows ?? DAYS_SKELETON_ROWS, DAYS_SKELETON_MAX_ROWS);
  return (
    <RecordCardSkeleton
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: DAYS_HEADER_PX, ml: 2 }}>
          <Skeleton width={110} />
        </Box>
      }
    >
      <Box sx={{ display: 'grid', gap: 1.5, minWidth: 0 }} data-days-card-skeleton>
        <Box sx={{ pt: `${DOT_ROOM_PX}px` }}>
          <Skeleton variant="rounded" height={RIBBON_HEIGHT_PX} sx={{ borderRadius: '3px' }} />
        </Box>
        {n === 0 ? (
          // The outline's empty row ("No days yet."): body2 with its 12 px padding.
          <Typography variant="body2" component="div" sx={{ p: 1.5 }} data-days-skeleton-empty>
            <Skeleton width={96} />
          </Typography>
        ) : (
          <Box>
            {Array.from({ length: n }, (_, i) => (
              <Box
                key={i}
                data-days-skeleton-row
                sx={{
                  height: DAY_ROW_PX,
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1,
                  ...(i > 0 ? { borderTop: 1, borderColor: 'divider' } : {}),
                }}
              >
                <Skeleton width={44} sx={{ flex: 'none' }} />
                <Skeleton width="30%" />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </RecordCardSkeleton>
  );
}

/** "8 rows · 12 days", after the duration check's note when it disagrees. */
const DaysCaption = memo(function DaysCaption({ id, ownerRec }: { id: string; ownerRec: RecordKey }) {
  const { baselines } = useOutline();
  const rows = useDraftSelector((entries) => dayRows(entries, baselines, ownerRec), dayRowsKey);
  const duration = useOutlineField(ownerRec, 'duration_display').value;
  const note = durationMismatch(typeof duration === 'string' ? duration : null, totalDays(rows));
  return (
    <Box
      data-days-caption
      sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: DAYS_HEADER_PX, minWidth: 0, ml: 2 }}
    >
      {note && (
        <Typography
          variant="caption"
          color="text.secondary"
          role="note"
          title={note}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}
          data-duration-note
        >
          <InfoOutlinedIcon aria-hidden sx={{ fontSize: 14, flex: 'none' }} />
          <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {note}
          </Box>
        </Typography>
      )}
      <Typography
        id={id}
        variant="caption"
        color="text.secondary"
        sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', flex: 'none' }}
      >
        {daysCaption(rows)}
        {note && <Box component="span" sx={visuallyHidden}>{` · ${note}`}</Box>}
      </Typography>
    </Box>
  );
});

/** Edit mode's **Add day**: exactly the header's 24 px reserve (a small text button with the
 *  plus — a draft add, saved with the rest); disabled at the 365-day cap, saying so. */
const AddDayButton = memo(function AddDayButton({ ownerRec, onAdd }: { ownerRec: RecordKey; onAdd: () => void }) {
  const { baselines } = useOutline();
  const kept = useDraftSelector(
    (entries) => listIn(DAYS_OUTLINE, entries, baselines, ownerRec, DAYS_PATH).length,
    (n) => String(n),
  );
  const refused = addDayRefusal(kept);
  const button = (
    <Button
      size="small"
      startIcon={<AddIcon />}
      onClick={() => onAdd()}
      disabled={refused !== null}
      data-add-day
      sx={{ height: DAYS_HEADER_PX, minHeight: DAYS_HEADER_PX, py: 0, px: 1, flex: 'none', whiteSpace: 'nowrap' }}
    >
      Add day
    </Button>
  );
  return refused ? (
    <Tooltip title={refused}>
      <span>{button}</span>
    </Tooltip>
  ) : (
    button
  );
});

/** The Day ribbon over the outline's rows (titles and spans as the draft has them), described
 *  by the outline (`describedBy`, its landmark's id — drawn whenever the ribbon has days). */
const DaysRibbon = memo(function DaysRibbon({
  ownerRec,
  openDay,
  onOpen,
  describedBy,
}: {
  ownerRec: RecordKey;
  openDay: number | null;
  onOpen: (dayId: number) => void;
  describedBy: string;
}) {
  const { baselines } = useOutline();
  // The rows and, in edit mode, each day's mark: added, or changed with its count (a removed
  // day's is the model's).
  const data = useDraftSelector(
    (entries) => {
      const rows = dayRows(entries, baselines, ownerRec);
      const marks: Record<number, DayMarks[number]> = {};
      for (const r of entries ? rows : []) {
        if (r.removed) continue;
        if (r.id < 0) marks[r.id] = { state: 'added' };
        else {
          const changes = Object.keys(entries?.[dayRec(r.id)] ?? {}).length;
          if (changes > 0) marks[r.id] = { state: 'changed', changes };
        }
      }
      return { rows, marks };
    },
    (d) => `${ribbonKey(d.rows)}|${JSON.stringify(d.marks)}`,
  );
  // A removed day keeps the days it covers as stored; the rows the draft keeps, the draft's.
  const stored = useMemo(() => storedDayRanges(baselines, ownerRec), [baselines, ownerRec]);
  const { days, marks } = useMemo(() => ribbonInput(data.rows, data.marks, stored), [data, stored]);
  return (
    <DayRibbon
      days={days}
      openDayId={openDay}
      onOpen={onOpen}
      describedBy={describedBy}
      {...(Object.keys(marks).length > 0 ? { marks } : {})}
    />
  );
});

/** The days as a records outline: one level, never virtualised. Without days, its empty row
 *  in both modes — "No days yet.", and in edit mode where to add the first — so pressing E
 *  moves nothing (an itinerary without a catalog itinerary has none). Live (edit mode,
 *  allowed): the row menus, Restore, Alt+arrows, Delete and the drag — each a `dayOps` edit. */
const DaysOutline = memo(function DaysOutline({
  id,
  ownerRec,
  openDay,
  onSelect,
  verdictRoom,
  imageOf,
  edit,
  onAdd,
  dayVerdict,
}: {
  /** The landmark's id (it describes the ribbon). */
  id: string;
  ownerRec: RecordKey;
  openDay: number | null;
  onSelect: (s: OutlineSelection<'day'>) => void;
  verdictRoom: boolean;
  /** A day's stored image (the detail's CDN keys). */
  imageOf: (dayId: number) => ImageChunks | null;
  edit: (op: (ctx: OutlineOpsContext) => DayEdit | null) => DayEdit | null;
  onAdd: (afterId?: number) => void;
  dayVerdict: DayVerdictOf;
}) {
  const { baselines, editing, canEdit } = useOutline();
  const live = editing && canEdit;
  const rows = useDraftSelector((entries) => dayRows(entries, baselines, ownerRec), dayRowsKey);
  const kept = useMemo(() => rows.filter((r) => !r.removed), [rows]);
  const keyMove = useMemo(
    () => ({
      accepts: (r: DayRowModel) => r.index !== null,
      move: (r: DayRowModel, delta: -1 | 1) => edit((ctx) => moveDay(ctx, r.id, r.index! + delta)) !== null,
    }),
    [edit],
  );
  const onRemoveKey = useCallback((r: DayRowModel) => edit((ctx) => removeDay(ctx, r.id)) !== null, [edit]);
  // A removed day does not open: there is nothing to edit on it until it is restored.
  const select = useCallback(
    (s: OutlineSelection<'day'>) => {
      if (s && rows.find((r) => r.id === s.id)?.removed) return;
      onSelect(s);
    },
    [rows, onSelect],
  );
  const drag = useMemo<OutlineDrag<DayRowModel>>(
    () => ({
      instructions: DRAG_INSTRUCTIONS,
      announce: (phase, from, over) => {
        if (!from) return undefined;
        const name = dayRowName(from);
        if (phase === 'start') return `Picked up ${name}. Drop it on another day to put it in that place.`;
        if (phase === 'cancel' || !over || over.removed || over.index === null || over.id === from.id) {
          return phase === 'over' ? undefined : `${name} was not moved.`;
        }
        return `${name} ${phase === 'end' ? 'moved' : 'moves'} to ${dayLabel(over.range)}.`;
      },
      onDrop: (from, over) => {
        if (!from || !over || from.removed || over.removed || over.index === null || from.id === over.id) return;
        edit((ctx) => moveDay(ctx, from.id, over.index!));
      },
      overlay: (r) => ({ name: dayRowName(r), strong: true }),
    }),
    [edit],
  );
  // The span labels' column: as wide as the longest label (tabular digits; one ch of slack).
  const labelCh = useMemo(() => Math.max(5, ...rows.map((r) => dayLabel(r.range).length)) + 1, [rows]);
  const selection = useMemo<OutlineSelection<'day'>>(() => (openDay === null ? null : { kind: 'day', id: openDay }), [openDay]);
  const renderRow = (r: DayRowModel, props: OutlineRowProps) => {
    const name = dayRowName(r);
    const actions = !live ? null : r.removed ? (
      <RestoreButton name={name} hint="keep this day" tabIndex={-1} onRestore={() => edit((ctx) => restoreDay(ctx, r.id))} />
    ) : (
      <OutlineRowMenu
        name={name}
        items={(close) => [
          <MenuItem key="insert" onClick={() => { close(); onAdd(r.id); }}>
            Insert a day after
          </MenuItem>,
          <MenuItem key="up" disabled={r.index === 0} onClick={() => { close(); edit((ctx) => moveDay(ctx, r.id, r.index! - 1)); }}>
            Move up
          </MenuItem>,
          <MenuItem key="down" disabled={r.index === kept.length - 1} onClick={() => { close(); edit((ctx) => moveDay(ctx, r.id, r.index! + 1)); }}>
            Move down
          </MenuItem>,
          ...(kept.length > 2
            ? [
                <ListSubheader key="to" sx={{ lineHeight: '32px' }}>
                  Move to
                </ListSubheader>,
                ...kept
                  .filter((k) => k.id !== r.id && k.index !== null && Math.abs(k.index - r.index!) > 1)
                  .map((k) => (
                    <MenuItem key={`to-${k.id}`} onClick={() => { close(); edit((ctx) => moveDay(ctx, r.id, k.index!)); }}>
                      {`${dayLabel(k.range)}’s place${k.title ? ` (${k.title})` : ''}`}
                    </MenuItem>
                  )),
              ]
            : []),
          <MenuItem key="remove" onClick={() => { close(); edit((ctx) => removeDay(ctx, r.id)); }} sx={{ color: 'error.main' }}>
            {`Remove ${name}`}
          </MenuItem>,
        ]}
      />
    );
    return (
      <DayRow
        key={r.id}
        row={r}
        {...props}
        open={!r.removed && r.id === openDay}
        editing={editing}
        live={live}
        verdictRoom={verdictRoom}
        storedImage={imageOf(r.id)}
        actions={actions}
        dayVerdict={dayVerdict}
      />
    );
  };
  return (
    <Box
      data-days-outline
      sx={{ containerType: 'inline-size', containerName: 'days', minWidth: 0 }}
      style={{ '--day-label-w': `${labelCh}ch` } as CSSProperties}
    >
      <RecordOutline
        id={id}
        rows={rows}
        selection={selection}
        onSelect={select}
        rowHeight={rowHeight}
        renderRow={renderRow}
        live={live}
        keyMove={keyMove}
        onRemoveKey={onRemoveKey}
        drag={drag}
        labels={LABELS}
        // One day opens in place, taller than a row: the virtualiser's fixed heights would not
        // hold, and an itinerary has at most 365 rows.
        virtualizeAbove={Infinity}
        // The rows sit on the card itself: no inset, and no scroller of their own.
        flush
      />
    </Box>
  );
});
